import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Alert, Spinner, Row, Col } from 'react-bootstrap';
import { Phone, HelpCircle, Eye, EyeOff, Copy, Check, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useFieldValidation } from '../hooks/useFieldValidation';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import TokenGenerator from '../components/whatsapp/TokenGenerator';
import WebhookInfoCard from '../components/whatsapp/WebhookInfoCard';
import ConnectionTestSection from '../components/whatsapp/ConnectionTestSection';
import ConfigurationChecklist, { ChecklistItem } from '../components/whatsapp/ConfigurationChecklist';
import HelpAccordion from '../components/whatsapp/HelpAccordion';

interface WhatsappConfig {
    api_token: string;
    phone_number_id: string;
    verification_token: string;
    template_names: Record<string, string>;
    is_active?: boolean;
    last_validated?: string | null;
    validation_error?: string | null;
}

const REQUIRED_TEMPLATES = [
    { key: 'login', label: 'Template de Login (Link Mágico)', description: 'Nome do template para enviar links de autenticação.' },
    { key: 'confirmation', label: 'Template de Confirmação', description: 'Nome do template para confirmar agendamentos.' },
    { key: 'cancellation', label: 'Template de Cancelamento', description: 'Nome do template para notificar cancelamentos.' },
];

// Template examples for Meta Business Manager
const TEMPLATE_EXAMPLES = {
    login: {
        name: 'login_magic_link',
        category: 'AUTHENTICATION',
        language: 'pt_BR',
        content: `Olá! 👋

Seu link de acesso ao sistema:

{{1}}

⏱️ Válido por 15 minutos
🔒 Não compartilhe com ninguém`,
        variables: [
            { position: 1, name: 'link', description: 'Link mágico de acesso gerado pelo sistema' }
        ],
        note: 'Crie no Meta com 1 variável de texto no corpo'
    },
    confirmation: {
        name: 'agendamento_confirmado',
        category: 'APPOINTMENT_UPDATE',
        language: 'pt_BR',
        content: `✅ Agendamento Confirmado!

Profissional: {{1}}
Serviço: {{2}}
Data/Hora: {{3}}

Aguardamos você! 🗓️`,
        variables: [
            { position: 1, name: 'professional_name', description: 'Nome do profissional' },
            { position: 2, name: 'service_name', description: 'Nome do serviço agendado' },
            { position: 3, name: 'date_time', description: 'Data e hora do agendamento (ex: 15/02/2025 às 14:30)' }
        ],
        note: 'Crie no Meta com 3 variáveis de texto no corpo'
    },
    cancellation: {
        name: 'agendamento_cancelado',
        category: 'APPOINTMENT_UPDATE',
        language: 'pt_BR',
        content: `❌ Agendamento Cancelado

Serviço: {{1}}
Data/Hora: {{2}}

Para reagendar, entre em contato conosco.`,
        variables: [
            { position: 1, name: 'service_name', description: 'Nome do serviço cancelado' },
            { position: 2, name: 'date_time', description: 'Data e hora do agendamento cancelado' }
        ],
        note: 'Crie no Meta com 2 variáveis de texto no corpo'
    }
};

const WhatsAppConfigPage = () => {
    const navigate = useNavigate();
    
    const [config, setConfig] = useState<WhatsappConfig>({
        api_token: '',
        phone_number_id: '',
        verification_token: '',
        template_names: {},
        is_active: false
    });
    
    const [originalConfig, setOriginalConfig] = useState<WhatsappConfig | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showToken, setShowToken] = useState(false);
    const [currentUser, setCurrentUser] = useState<{ is_staff?: boolean }>({});
    const [userLoading, setUserLoading] = useState(true);
    const [expandedTemplate, setExpandedTemplate] = useState<string | null>(null);
    const [copiedTemplate, setCopiedTemplate] = useState<string | null>(null);
    
    const { errors, validateField, clearAllErrors } = useFieldValidation();
    
    // Copy template content to clipboard
    const copyTemplate = (key: string, content: string) => {
        navigator.clipboard.writeText(content);
        setCopiedTemplate(key);
        setTimeout(() => setCopiedTemplate(null), 2000);
    };
    
    // Check if there are unsaved changes
    const hasChanges = originalConfig !== null && JSON.stringify(config) !== JSON.stringify(originalConfig);
    useUnsavedChanges(hasChanges);

    // Check if user is admin (same logic as MainLayout)
    const isAdmin = currentUser?.is_staff === true;

    useEffect(() => {
        fetchCurrentUser();
        fetchConfig();
    }, []);

    const fetchCurrentUser = async () => {
        try {
            const response = await api.get('/company/me/');
            setCurrentUser(response.data);
        } catch (err) {
            console.error('Error fetching current user:', err);
        } finally {
            setUserLoading(false);
        }
    };

    const fetchConfig = async () => {
        try {
            const response = await api.get('/whatsapp/config/');
            if (response.data) {
                const data = response.data;
                if (!data.template_names) data.template_names = {};
                setConfig(data);
                setOriginalConfig(data);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setConfig({ ...config, [name]: value });
        
        // Validate field on change
        if (name === 'api_token' || name === 'phone_number_id' || name === 'verification_token') {
            validateField(name, value);
        }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        if (name === 'api_token' || name === 'phone_number_id' || name === 'verification_token') {
            validateField(name, value);
        }
    };

    const handleTemplateChange = (key: string, value: string) => {
        setConfig(prev => ({
            ...prev,
            template_names: {
                ...prev.template_names,
                [key]: value
            }
        }));
    };

    const handleToggleActive = (e: React.ChangeEvent<HTMLInputElement>) => {
        setConfig({ ...config, is_active: e.target.checked });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        setSuccess('');
        clearAllErrors();

        // Validate all fields
        const apiTokenValid = validateField('api_token', config.api_token);
        const phoneIdValid = validateField('phone_number_id', config.phone_number_id);
        const verificationTokenValid = validateField('verification_token', config.verification_token);

        if (!apiTokenValid || !phoneIdValid || !verificationTokenValid) {
            setError('Por favor, corrija os erros nos campos destacados.');
            setSaving(false);
            return;
        }

        try {
            await api.put('/whatsapp/config/', config);
            setSuccess('Configuração salva com sucesso!');
            setOriginalConfig(config); // Update original config after save
            
            // Fetch updated config to get last_validated if backend updated it
            setTimeout(fetchConfig, 1000);
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || err.response?.data?.error || 'Erro ao salvar configuração.';
            setError(errorMessage);
            console.error(err);
        } finally {
            setSaving(false);
        }
    };

    // Generate checklist items
    const getChecklistItems = (): ChecklistItem[] => {
        return [
            {
                id: 'api_token',
                label: 'API Token configurado',
                completed: (config.api_token?.length ?? 0) >= 20 && config.api_token?.startsWith('EAA'),
                error: !!errors.api_token
            },
            {
                id: 'phone_number_id',
                label: 'Phone Number ID configurado',
                completed: (config.phone_number_id?.length ?? 0) >= 10 && /^\d+$/.test(config.phone_number_id || ''),
                error: !!errors.phone_number_id
            },
            {
                id: 'verification_token',
                label: 'Verification Token gerado',
                completed: (config.verification_token?.length ?? 0) >= 10,
                error: !!errors.verification_token
            },
            {
                id: 'config_saved',
                label: 'Configuração salva',
                completed: !hasChanges && originalConfig !== null
            },
            {
                id: 'webhook_configured',
                label: 'Webhook configurado no Meta',
                completed: false // This must be done manually by user
            },
            {
                id: 'connection_tested',
                label: 'Conexão testada com sucesso',
                completed: config.last_validated !== null && config.last_validated !== undefined && !config.validation_error
            }
        ];
    };

    // Show permission denied screen if not admin (wait for both loading states)
    if (!loading && !userLoading && !isAdmin) {
        return (
            <Container className="p-4 text-center" style={{ maxWidth: '600px' }}>
                <div className="py-5">
                    <div className="mb-4">
                        <div 
                            className="d-inline-flex align-items-center justify-content-center rounded-circle bg-light"
                            style={{ width: '100px', height: '100px' }}
                        >
                            <Phone size={50} className="text-muted" />
                        </div>
                    </div>
                    <h3 className="mb-3">Acesso Restrito</h3>
                    <p className="text-muted mb-4">
                        Apenas administradores podem configurar o WhatsApp Business.
                        <br />
                        Entre em contato com um administrador do sistema.
                    </p>
                    <Button variant="primary" onClick={() => navigate(-1)}>
                        Voltar ao Painel
                    </Button>
                </div>
            </Container>
        );
    }

    if (loading || userLoading) {
        return (
            <Container className="p-4 text-center">
                <Spinner animation="border" />
                <p className="mt-3 text-muted">Carregando configuração...</p>
            </Container>
        );
    }

    return (
        <Container className="p-4" style={{ maxWidth: '900px' }}>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="d-flex align-items-center gap-2">
                    <Phone size={28} />
                    Configuração do WhatsApp Business
                </h2>
                <Button 
                    variant="outline-info" 
                    onClick={() => navigate('/whatsapp/help')} 
                    className="d-flex align-items-center gap-2"
                >
                    <HelpCircle size={20} /> Ajuda Completa
                </Button>
            </div>

            {/* Alerts */}
            {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
            {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}
            {hasChanges && (
                <Alert variant="warning">
                    <strong>Atenção:</strong> Você tem alterações não salvas.
                </Alert>
            )}

            <Form onSubmit={handleSubmit}>
                {/* Credentials Section */}
                <Card className="mb-4">
                    <Card.Header>
                        <h5 className="mb-0">Credenciais da API Meta</h5>
                    </Card.Header>
                    <Card.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>
                                Phone Number ID
                                <span className="text-danger"> *</span>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                name="phone_number_id"
                                value={config.phone_number_id}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                                isInvalid={!!errors.phone_number_id}
                                placeholder="123456789012345"
                            />
                            {errors.phone_number_id && (
                                <Form.Control.Feedback type="invalid">
                                    {errors.phone_number_id}
                                </Form.Control.Feedback>
                            )}
                            {!errors.phone_number_id && (
                                <Form.Text className="text-muted">
                                    ID do número de telefone obtido no Meta Developers
                                </Form.Text>
                            )}
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>
                                API Access Token
                                <span className="text-danger"> *</span>
                            </Form.Label>
                            <div className="position-relative">
                                <Form.Control
                                    type={showToken ? "text" : "password"}
                                    name="api_token"
                                    value={config.api_token}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    required
                                    isInvalid={!!errors.api_token}
                                    placeholder="EAAxxxxxxxx..."
                                    style={{ paddingRight: '45px' }}
                                />
                                <Button
                                    variant="link"
                                    className="position-absolute end-0 top-0 text-muted"
                                    onClick={() => setShowToken(!showToken)}
                                    style={{ 
                                        zIndex: 10,
                                        padding: '0.375rem 0.75rem'
                                    }}
                                    title={showToken ? "Ocultar token" : "Mostrar token"}
                                >
                                    {showToken ? <EyeOff size={18} /> : <Eye size={18} />}
                                </Button>
                            </div>
                            {errors.api_token && (
                                <div className="invalid-feedback d-block">
                                    {errors.api_token}
                                </div>
                            )}
                            {!errors.api_token && (
                                <Form.Text className="text-muted">
                                    Token de acesso obtido em{' '}
                                    <a 
                                        href="https://developers.facebook.com" 
                                        target="_blank" 
                                        rel="noreferrer"
                                    >
                                        developers.facebook.com
                                    </a>
                                </Form.Text>
                            )}
                        </Form.Group>

                        <TokenGenerator
                            value={config.verification_token}
                            onChange={(value) => setConfig({ ...config, verification_token: value })}
                            label="Verification Token"
                            helpText="Token usado pelo Meta para verificar o webhook (recomendado 32 caracteres)"
                            required
                            isInvalid={!!errors.verification_token}
                            errorMessage={errors.verification_token}
                        />

                        <Form.Group className="mb-0">
                            <Form.Check
                                type="switch"
                                id="is_active"
                                label="Ativar integração WhatsApp"
                                checked={config.is_active || false}
                                onChange={handleToggleActive}
                            />
                            <Form.Text className="text-muted">
                                Quando desmarcado, as mensagens não serão processadas
                            </Form.Text>
                        </Form.Group>
                    </Card.Body>
                </Card>

                {/* Webhook Info */}
                <WebhookInfoCard />

                {/* Templates Section */}
                <Card className="mb-4">
                    <Card.Header>
                        <h5 className="mb-0">Mapeamento de Templates</h5>
                    </Card.Header>
                    <Card.Body>
                        <Alert variant="info" className="mb-4">
                            <div className="d-flex align-items-start gap-2">
                                <FileText size={20} className="mt-1 flex-shrink-0" />
                                <div>
                                    <strong>Como criar templates no Meta:</strong>
                                    <ol className="mb-0 mt-2 ps-3">
                                        <li>Acesse o <a href="https://business.facebook.com/wa/manage/message-templates/" target="_blank" rel="noreferrer">Gerenciador de Templates</a></li>
                                        <li>Clique em "Criar modelo"</li>
                                        <li>Use os exemplos abaixo como base (copie o conteúdo)</li>
                                        <li>Adicione as variáveis {'{{1}}'}, {'{{2}}'}, etc. no corpo da mensagem</li>
                                        <li>Salve e aguarde aprovação do Meta</li>
                                        <li>Informe o nome exato do template aprovado nos campos abaixo</li>
                                    </ol>
                                </div>
                            </div>
                        </Alert>

                        <Row>
                            {REQUIRED_TEMPLATES.map((tpl) => {
                                const example = TEMPLATE_EXAMPLES[tpl.key as keyof typeof TEMPLATE_EXAMPLES];
                                const isExpanded = expandedTemplate === tpl.key;
                                
                                return (
                                    <Col md={12} key={tpl.key} className="mb-4">
                                        <Card className="border-light bg-light">
                                            <Card.Body>
                                                <Form.Group className="mb-3">
                                                    <Form.Label className="fw-bold">{tpl.label}</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        placeholder={`Ex: ${example.name}`}
                                                        value={config.template_names[tpl.key] || ''}
                                                        onChange={(e) => handleTemplateChange(tpl.key, e.target.value)}
                                                    />
                                                    <Form.Text className="text-muted">
                                                        {tpl.description}
                                                    </Form.Text>
                                                </Form.Group>

                                                {/* Template Example */}
                                                <div className="mt-3">
                                                    <Button
                                                        variant="outline-secondary"
                                                        size="sm"
                                                        onClick={() => setExpandedTemplate(isExpanded ? null : tpl.key)}
                                                        className="d-flex align-items-center gap-2"
                                                    >
                                                        <FileText size={16} />
                                                        {isExpanded ? 'Ocultar exemplo' : 'Ver exemplo para criar no Meta'}
                                                    </Button>

                                                    {isExpanded && (
                                                        <div className="mt-3 p-3 bg-white border rounded">
                                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                                <div>
                                                                    <strong className="text-primary">{example.name}</strong>
                                                                    <div className="small text-muted">
                                                                        Categoria: {example.category} | Idioma: {example.language}
                                                                    </div>
                                                                </div>
                                                                <Button
                                                                    variant="outline-primary"
                                                                    size="sm"
                                                                    onClick={() => copyTemplate(tpl.key, example.content)}
                                                                    className="d-flex align-items-center gap-1"
                                                                >
                                                                    {copiedTemplate === tpl.key ? (
                                                                        <><Check size={16} /> Copiado!</>
                                                                    ) : (
                                                                        <><Copy size={16} /> Copiar texto</>
                                                                    )}
                                                                </Button>
                                                            </div>
                                                            
                                                            <pre className="bg-light p-3 rounded mb-3" style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>
                                                                {example.content}
                                                            </pre>
                                                            
                                                            <div className="small">
                                                                <strong>Variáveis:</strong>
                                                                <ul className="mb-2">
                                                                    {example.variables.map((v) => (
                                                                        <li key={v.position}>
                                                                            <code>{'{{'}{v.position}{'}}'}</code> - {v.description}
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                                <div className="text-muted">
                                                                    <strong>Nota:</strong> {example.note}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>

                        <div className="d-grid gap-2 d-md-flex justify-content-md-end mt-4">
                            <Button 
                                variant="primary" 
                                type="submit" 
                                disabled={saving || !hasChanges} 
                                size="lg"
                                className="px-4"
                            >
                                {saving ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Salvando...
                                    </>
                                ) : (
                                    'Salvar Configuração'
                                )}
                            </Button>
                        </div>
                    </Card.Body>
                </Card>
            </Form>

            {/* Connection Test Section */}
            {originalConfig && (
                <ConnectionTestSection
                    lastValidated={config.last_validated}
                    validationError={config.validation_error}
                />
            )}

            {/* Configuration Checklist */}
            <ConfigurationChecklist items={getChecklistItems()} />

            {/* Help Accordion */}
            <HelpAccordion />
        </Container>
    );
};

export default WhatsAppConfigPage;
